import React, { useEffect, useState } from "react";
import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import "jspdf-autotable";
import CardBox from "./CardBox";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { useRouteMatch } from "react-router-dom";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import ValidationMessage from "../../../../components/validation/ValidationMessage";

interface AlertConfigNodeProps {
  isOpen: any;
  setIsOpen: any;
  handleSubmit: any;
  errorMessage: any;
  setErrorMessage: any;
  alertName: any;
  setAlertName: any;
  stationNode: any;
  isLoading: any;
}

const useStyles = makeStyles((theme: CustomTheme) => createStyles({}));
type StationMetrics = {
  [code: string]: Threshold & {
    name: string;
    code: string;
  };
};

type StationInfo = {
  [id: number]: {
    code: string;
    name: string;
  };
};

type Threshold = {
  warningThreshold: number;
  criticalThreshold: number;
};
const mergeStationMetrics = (
  input: StationInfo,
  input1: any,
): { stationMetrics: StationMetrics } => {
  const result: any = {};
  for (const key in input) {
    const { code, name } = input[key];
    const thresholds = input1?.[code] || {};

    result[code] = {
      warningThreshold: thresholds?.warningThreshold ?? 600,
      criticalThreshold: thresholds?.criticalThreshold ?? 720,
      name,
      code,
    };
  }

  return result;
};

const AlertConfigModal: React.FC<AlertConfigNodeProps> = ({
  isOpen,
  setIsOpen,
  handleSubmit,
  errorMessage,
  setErrorMessage,
  alertName,
  setAlertName,
  stationNode,
  isLoading,
}) => {
  const [alertConfigNode, setAlertConfigNode] = useState<any>({});
  const theme: CustomTheme = useTheme();
  const stationAverageTime = mergeStationMetrics(stationNode, {});
  const createdData = {
    preparationAverageTime: { warningThreshold: 600, criticalThreshold: 720 },
    locationAverageTime: { warningThreshold: 2700, criticalThreshold: 3600 },
    loadAverageTime: { warningThreshold: 300, criticalThreshold: 480 },
    driverAverageTime: { warningThreshold: 900, criticalThreshold: 1200 },
    stationAverageTime: stationAverageTime,
  };

  useEffect(() => {
    if (isOpen) {
      setAlertConfigNode(createdData);
    }
  }, [isOpen]);
  return (
    <>
      <DialogCommonDefault
        open={isOpen}
        setOpen={setIsOpen}
        isNeedFixedHeight={true}
        backgroundColor={theme.palette.background.entity_background}
        maxWidth={"md"}
      >
        <Grid container>
          <Grid
            item
            xs={6}
            style={{
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "20px",
            }}
          >
            <TextfieldCommon
              label="Name"
              type="text"
              value={alertName}
              onChange={(e: any) => {
                setErrorMessage({});
                setAlertName(e.target.value);
              }}
              style={{ width: "100%" }}
            />
            {errorMessage?.["alertName"]?.alertName && (
              <ValidationMessage message={"Name is required"} />
            )}
          </Grid>
          <Grid item xs={6}></Grid>
          <Grid item xs={6}>
            <CardBox
              title="Location Average Time"
              averageTimeType="locationAverageTime"
              data={alertConfigNode?.locationAverageTime || {}}
              setAlertConfigNode={setAlertConfigNode}
              errorMessage={errorMessage.locationAverageTime}
              setErrorMessage={setErrorMessage}
            />
          </Grid>
          <Grid item xs={6}>
            <CardBox
              title="Preparation Average Time"
              averageTimeType="preparationAverageTime"
              data={alertConfigNode?.preparationAverageTime || {}}
              setAlertConfigNode={setAlertConfigNode}
              errorMessage={errorMessage.preparationAverageTime}
              setErrorMessage={setErrorMessage}
            />
          </Grid>
          <Grid item xs={6}>
            <CardBox
              title="Driver Average Time"
              averageTimeType="driverAverageTime"
              data={alertConfigNode?.driverAverageTime || {}}
              setAlertConfigNode={setAlertConfigNode}
              errorMessage={errorMessage.driverAverageTime}
              setErrorMessage={setErrorMessage}
            />
          </Grid>
          <Grid item xs={6}> 
            <CardBox
              title="Load Average Time"
              averageTimeType={"loadAverageTime"}
              data={alertConfigNode?.loadAverageTime || {}}
              setAlertConfigNode={setAlertConfigNode}
              errorMessage={errorMessage.driverAverageTime}
              setErrorMessage={setErrorMessage}
            />
          </Grid>
          <Grid item xs={12} style={{ paddingLeft: "20px" }}>
            <Typography variant="h6" align="left">
              Station Average Time
            </Typography>
          </Grid>
          {alertConfigNode?.stationAverageTime &&
            Object.entries(alertConfigNode?.stationAverageTime).map(
              ([key, item]: any) => (
                <Grid item xs={6}>
                  <CardBox
                    title={item.name}
                    averageTimeType={"stationAverageTime"}
                    data={item}
                    setAlertConfigNode={setAlertConfigNode}
                    errorMessage={errorMessage.stationAverageTime}
                    setErrorMessage={setErrorMessage}
                    station={key}
                  />
                </Grid>
              ),
            )}

          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "end",
              paddingRight: "20px",
              paddingBottom: "20px",
            }}
          >
            <ButtonCommon
              variant="contained"
              style={{ fontSize: 11, width: "100px", marginRight: 8 }}
              color={buttonColors.CANCEL_BUTTON_COLOR}
              onClick={() => {
                setAlertName("");
                setIsOpen(false);
              }}
            >
              Cancel
            </ButtonCommon>
            <ButtonCommon
              variant="contained"
              style={{ fontSize: 11, width: "100px" }}
              color={buttonColors.CREATE_BUTTON_COLOR}
              onClick={() => handleSubmit("create", alertConfigNode)}
              isLoadingPage={isLoading}
            >
              Create
            </ButtonCommon>
          </Grid>
        </Grid>
      </DialogCommonDefault>
    </>
  );
};

export default AlertConfigModal;
